import { tt } from '@agos/shared'

/**
 * The module catalogue and the tier list, as the two `super-admin` endpoints answer them - §3.3.
 *
 * **Both pages read both payloads**, which is why this file exists rather than a helper beside each:
 * *Packages* needs the module catalogue to draw its picker, and *Modules* needs the packages to
 * answer the question it exists for - *which tiers include this module*.
 *
 * Measured on the real registry: **201** modules in **12** categories, **6** `alwaysOn`, **123** with
 * a `parentKey`, all 201 carrying a description; **3** packages - Basic 149,00 € with 29 modules,
 * Štandard 399,00 € with 89, Premium 499,00 € with 200.
 */

export interface CatalogModule {
  key: string
  parentKey: string | null
  name: string
  description: string | null
  category: string
  alwaysOn: boolean
  /**
   * Whether the **API** refuses a franchise whose tier excludes this - §3.72, owner decision #25.
   *
   * Not the same question as `alwaysOn`, which is about what a tier may withhold. This is about
   * whether withholding it is *felt*: 21 of the 210 keys carry a `@RequireModule` on an endpoint;
   * the rest gate the manager's sidebar only, so typing the address still reaches the screen.
   *
   * **Computed by the server from its own routing table** rather than maintained anywhere, because
   * the figure written into `SAAS.md §3.17` went stale by eighteen in four days. Absent on a payload
   * from a server that predates §3.72, and `false` is the safe reading of that: it claims no
   * enforcement that has not been demonstrated.
   */
  enforced: boolean
  sortOrder: number
}

export interface CatalogPackage {
  id: number
  slug: string
  name: string
  description: string | null
  /** A decimal string on the wire - `"149.00"`. Kept as sent: it is edited as text. */
  monthlyPrice: string
  defaultTheme: string
  isActive: boolean
  sortOrder: number
  modules: string[]
}

/**
 * The order the categories are drawn in, and it is **not** alphabetical.
 *
 * It is the shape of the product: from what every franchise has (`core`) to what almost none does
 * (`infra`). Legacy's own order, and sorting these by name would scatter *Rozvoz* between *Analytika*
 * and *Katalóg*.
 */
export const CATEGORY_ORDER = [
  'core',
  'operations',
  'catalog',
  'specials',
  'delivery',
  'loyalty',
  'payments',
  'analytics',
  'campaigns',
  'customer',
  'settings',
  'infra',
  'other',
] as const

/** Legacy's labels. The raw keys were on screen before §3.3, which is a database word in a UI. */
export const CATEGORY_LABELS: Record<string, string> = {
  core: tt('Jadro'),
  operations: tt('Prevádzka'),
  catalog: tt('Katalóg'),
  delivery: tt('Rozvoz'),
  specials: tt('Špeciály'),
  loyalty: tt('Vernosť a promo'),
  analytics: tt('Analytika a štatistiky'),
  campaigns: tt('Kampane'),
  customer: tt('Zákaznícka stránka'),
  payments: tt('Platby'),
  settings: tt('Nastavenia'),
  infra: tt('Infraštruktúra'),
  other: tt('Ostatné'),
}

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function orNull(value: unknown): string | null {
  const read = text(value).trim()
  return read === '' ? null : read
}

function count(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function modulesOf(payload: unknown): CatalogModule[] {
  const body = (typeof payload === 'object' && payload !== null ? payload : {}) as { modules?: unknown }
  const rows = Array.isArray(body.modules) ? body.modules : []
  return rows
    .map((entry) => {
      const row = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
      return {
        key: text(row.key),
        parentKey: orNull(row.parentKey),
        name: text(row.name),
        description: orNull(row.description),
        category: text(row.category) === '' ? 'other' : text(row.category),
        alwaysOn: row.alwaysOn === true,
        enforced: row.enforced === true,
        sortOrder: count(row.sortOrder),
      }
    })
    .filter((module) => module.key !== '')
}

export function packagesOf(payload: unknown): CatalogPackage[] {
  const body = (typeof payload === 'object' && payload !== null ? payload : {}) as { packages?: unknown }
  const rows = Array.isArray(body.packages) ? body.packages : []
  return rows
    .map((entry) => {
      const row = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
      return {
        id: count(row.id),
        slug: text(row.slug),
        name: text(row.name).trim(),
        description: orNull(row.description),
        monthlyPrice: typeof row.monthlyPrice === 'number' ? String(row.monthlyPrice) : text(row.monthlyPrice),
        defaultTheme: text(row.defaultTheme) === '' ? 'classic' : text(row.defaultTheme),
        isActive: row.isActive === true,
        sortOrder: count(row.sortOrder),
        modules: Array.isArray(row.modules) ? row.modules.map(text).filter((key) => key !== '') : [],
      }
    })
    .filter((one) => one.id > 0)
    .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, 'sk'))
}

export interface CategoryGroup {
  category: string
  label: string
  modules: CatalogModule[]
}

/**
 * The catalogue grouped for drawing: categories in `CATEGORY_ORDER`, then any the order does not
 * name, and inside each, **a parent immediately followed by its own children**.
 *
 * Sorting a category by `sortOrder` alone puts children wherever the seed happened to number them;
 * 123 of the 201 modules have a parent, so that is most of the list reading as a flat jumble.
 */
export function groupByCategory(modules: readonly CatalogModule[]): CategoryGroup[] {
  const byCategory = new Map<string, CatalogModule[]>()
  for (const module of modules) {
    byCategory.set(module.category, [...(byCategory.get(module.category) ?? []), module])
  }

  const named = new Set<string>(CATEGORY_ORDER)
  const ordered = [
    ...CATEGORY_ORDER.filter((category) => byCategory.has(category)),
    ...[...byCategory.keys()].filter((category) => !named.has(category)).sort(),
  ]

  return ordered.map((category) => ({
    category,
    label: categoryLabel(category),
    modules: nestChildren(byCategory.get(category) ?? []),
  }))
}

/** Parents in `sortOrder`, each trailed by its children in `sortOrder`. */
function nestChildren(modules: readonly CatalogModule[]): CatalogModule[] {
  const bySort = (left: CatalogModule, right: CatalogModule) =>
    left.sortOrder - right.sortOrder || left.key.localeCompare(right.key)
  const own = new Set(modules.map((module) => module.key))
  // A child whose parent is in another category has nobody to sit under here, so it stands alone.
  const roots = modules.filter((module) => module.parentKey === null || !own.has(module.parentKey))
  const out: CatalogModule[] = []
  for (const root of [...roots].sort(bySort)) {
    out.push(root)
    out.push(...modules.filter((module) => module.parentKey === root.key).sort(bySort))
  }
  return out
}

export interface ModuleSummary {
  /** What to draw, in the picker's own order, capped. */
  labels: string[]
  /** How many are not drawn. Zero when they all fit. */
  extra: number
}

/**
 * What a tier includes, short enough to read on its card - §3.138.
 *
 * **Legacy lists the raw keys and this names them.** Both pages already load the catalogue, so the
 * card can say *Kanban objednávok* where legacy says `orders_kanban`. That is the one departure from
 * the port, and it is free.
 *
 * **The order is `groupByCategory`'s, passed in rather than re-derived.** A package's `modules` array
 * comes out of a SQL join with nothing ordering it, so *the first ten* off that array would be
 * whichever ten the database happened to return - and with a cap, which ten is the whole question.
 * Walking the picker's own grouping makes the card's first ten **the picker's** first ten by
 * construction, so no later sort can put the two out of step.
 *
 * **A key the catalogue does not describe is kept, shown bare, and sorted last.** It means a tier
 * referencing a module that no longer exists, which is precisely the row worth seeing; dropping it
 * would also leave the list disagreeing with the count printed beside it.
 */
export function packageModuleSummary(
  chosen: readonly string[],
  groups: readonly CategoryGroup[],
  limit = 10,
): ModuleSummary {
  const wanted = new Set(chosen)
  const found = new Set<string>()
  const named: string[] = []
  for (const group of groups) {
    for (const module of group.modules) {
      if (!wanted.has(module.key) || found.has(module.key)) continue
      found.add(module.key)
      // `modulesOf` requires a key and not a name, so an unnamed module is a real payload.
      named.push(module.name === '' ? module.key : module.name)
    }
  }
  const orphaned = [...wanted].filter((key) => !found.has(key))
  const all = [...named, ...orphaned]
  return { labels: all.slice(0, limit), extra: Math.max(0, all.length - limit) }
}

/** Which packages include a module - derived here, because no endpoint answers it. */
export function packagesByModule(packages: readonly CatalogPackage[]): Map<string, CatalogPackage[]> {
  const map = new Map<string, CatalogPackage[]>()
  for (const one of packages) {
    for (const key of one.modules) {
      map.set(key, [...(map.get(key) ?? []), one])
    }
  }
  return map
}

/** Legacy searches key, name **and** description - the three things a module is recognised by. */
export function matchesModuleSearch(module: CatalogModule, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return (
    module.key.toLowerCase().includes(needle) ||
    module.name.toLowerCase().includes(needle) ||
    (module.description ?? '').toLowerCase().includes(needle)
  )
}

/**
 * What *Všetky* and *Žiadne* do to a category, and the asymmetry is the rule.
 *
 * *Všetky* adds every module in the category. **_Žiadne_ removes every one except the `alwaysOn`
 * ones** - legacy's own behaviour, and symmetry here would let one click strip a module the platform
 * says cannot be stripped.
 */
export function toggledCategory(
  chosen: readonly string[],
  categoryModules: readonly CatalogModule[],
  turnOn: boolean,
): string[] {
  const set = new Set(chosen)
  for (const module of categoryModules) {
    if (turnOn) set.add(module.key)
    else if (!module.alwaysOn) set.delete(module.key)
  }
  return [...set]
}

/** Whether every module of a category is chosen - what decides which word the toggle shows. */
export function categoryIsFull(
  chosen: readonly string[],
  categoryModules: readonly CatalogModule[],
): boolean {
  if (categoryModules.length === 0) return false
  const set = new Set(chosen)
  return categoryModules.every((module) => set.has(module.key))
}

export function toggledModule(chosen: readonly string[], key: string): string[] {
  const set = new Set(chosen)
  if (set.has(key)) set.delete(key)
  else set.add(key)
  return [...set]
}

/** `^[a-z0-9_-]+$`, legacy's own rule: a slug is what a franchise's `package` join is written against. */
export function slugProblem(slug: string): string | null {
  const read = slug.trim()
  if (read === '') return tt('Slug je povinný')
  if (!/^[a-z0-9_-]+$/.test(read)) return tt('Slug môže obsahovať len a-z, 0-9, _ a -')
  return null
}

export function hasName(name: string): boolean {
  return name.trim() !== ''
}

/** The two things a new package must have before it can be sent. */
export function draftProblem(draft: { slug: string; name: string }, isNew: boolean): string | null {
  if (!hasName(draft.name)) return tt('Názov je povinný')
  // Only on create: `PATCH` never sends the slug, so an existing package's slug is not editable.
  return isNew ? slugProblem(draft.slug) : null
}

/**
 * The id list a reorder sends, with one package moved a step.
 *
 * The **whole** list, because that is what `POST /packages/reorder` takes - the server reassigns
 * `sortOrder = (i + 1) * 10` across all of them. Answers `null` at the ends rather than a list that
 * has not moved, so the caller can leave the arrow disabled instead of sending a no-op.
 */
export function reorderedIds(
  packages: readonly CatalogPackage[],
  id: number,
  direction: -1 | 1,
): number[] | null {
  const from = packages.findIndex((one) => one.id === id)
  const to = from + direction
  if (from < 0 || to < 0 || to >= packages.length) return null
  const ids = packages.map((one) => one.id)
  const moved = ids[from]!
  ids[from] = ids[to]!
  ids[to] = moved
  return ids
}

/**
 * How much of the catalogue a tenant actually feels - §3.72, owner decision #25.
 *
 * **The sentence this page was missing.** It offered 210 switches with nothing to tell a reader
 * which of them a customer would notice, so somebody could sell a Basic tier believing all 210 hold.
 * The owner asked *"what does a tier buy?"* and the honest answer has two halves: the API refuses on
 * some of these, and gates the manager's sidebar on the rest.
 *
 * **Derived from the payload, never from a literal.** `EnforcedModules` computes the flag from the
 * routing table on every request; a count written here would be the same mistake one layer up -
 * §3.17's *"the API enforces 3"* was wrong by eighteen within four days of being recorded.
 */
export interface EnforcementSummary {
  total: number
  enforced: number
  menuOnly: number
}

export function enforcementSummary(modules: readonly CatalogModule[]): EnforcementSummary {
  const enforced = modules.filter((module) => module.enforced).length
  return { total: modules.length, enforced, menuOnly: modules.length - enforced }
}

/**
 * What to say about it, in one line over the list.
 *
 * Empty when nothing is loaded yet - a page that says *"0 z 0 modulov"* while a request is in flight
 * is stating something false about the catalogue rather than saying nothing.
 */
export function enforcementNote(summary: EnforcementSummary): string {
  if (summary.total === 0) return ''
  return `${tt('API vynucuje')} ${summary.enforced} ${tt('z')} ${summary.total} ${tt('modulov')}. ${tt('Ostatné rozhodujú len o tom, čo je v menu manažéra - adresu je stále možné otvoriť.')}`
}
